import React, { useEffect } from "react";
import { Box, Typography, CircularProgress, Tooltip, LinearProgress } from "@mui/material";
import { getUsersAction, updateUserAction } from "../redux/actions/user";
import { useDispatch, useSelector } from "react-redux";
import strings from "../i18n/language";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import FemaleRoundedIcon from "@mui/icons-material/FemaleRounded";
import MaleRoundedIcon from "@mui/icons-material/MaleRounded";
import dayjs from "dayjs";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const STATUS_META = {
  active:    { bg: 'rgba(91,191,181,0.14)', color: '#5bbfb5',  label: 'Activo' },
  inactive:  { bg: 'rgba(139,159,212,0.12)', color: '#8b9fd4', label: 'Inactivo' },
  suspended: { bg: 'rgba(224,92,92,0.14)',   color: '#e05c5c', label: 'Suspendido' },
  waiting:   { bg: 'rgba(255,193,7,0.14)',   color: '#ffc107', label: 'Pendiente' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { bg: GLASS, color: MUTED, label: status };
  return (
    <span style={{
      background: meta.bg,
      color: meta.color,
      borderRadius: 6,
      padding: '3px 9px',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
};

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px',
    fontSize: 13,
    color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`,
    verticalAlign: 'middle',
    ...style,
  }}>
    {children}
  </td>
);

const ActionBtn = ({ onClick, title, children, color }) => (
  <Tooltip title={title}>
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: MUTED,
        borderRadius: 6,
        width: 28, height: 28,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'color 0.15s, background 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = color; e.currentTarget.style.background = `${color}18`; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; e.currentTarget.style.background = 'none'; }}
    >
      {children}
    </button>
  </Tooltip>
);

const Users = () => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.user.users);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);
  const isLoadingUsers = useSelector((state) => state.user.isLoadingUsers);

  useEffect(() => {
    if (selectedCompany) dispatch(getUsersAction(selectedCompany.id));
  }, [selectedCompany, dispatch]);

  return (
    <Box sx={{ width: "100%" }}>
      <div style={{
        background: GLASS,
        border: `1px solid ${BORDER}`,
        borderRadius: 20,
        backdropFilter: 'blur(12px)',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: `1px solid ${BORDER}`,
        }}>
          <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
            Usuarios
          </Typography>
          {users && (
            <span style={{ fontSize: 12, color: MUTED }}>
              {users.length} {users.length === 1 ? 'usuario' : 'usuarios'}
            </span>
          )}
        </div>

        {/* Loading bar */}
        {isLoadingUsers && (
          <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { background: BRAND } }} />
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader>{strings.LABEL_USERS_HEADER_NAME}</ColHeader>
                <ColHeader>{strings.LABEL_USERS_HEADER_GENDER}</ColHeader>
                <ColHeader>{strings.LABEL_USERS_HEADER_DOB}</ColHeader>
                <ColHeader>{strings.LABEL_USERS_HEADER_PLATFORM}</ColHeader>
                <ColHeader>{strings.LABEL_USERS_HEADER_CONNECTIONS}</ColHeader>
                <ColHeader>Estado</ColHeader>
                <ColHeader style={{ width: 80 }}></ColHeader>
              </tr>
            </thead>
            <tbody>
              {(!users || !users.length) && !isLoadingUsers && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 40, fontSize: 13, color: MUTED }}>
                    {strings.LABEL_NO_RESULTS}
                  </td>
                </tr>
              )}
              {isLoadingUsers && !users && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 40 }}>
                    <CircularProgress size={24} sx={{ color: BRAND }} />
                  </td>
                </tr>
              )}
              {users && users.map((user) => (
                <tr key={user.id}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  style={{ transition: 'background 0.1s' }}
                >
                  <Td style={{ color: '#e8f0fe', fontWeight: 500 }}>
                    {user.displayName || <span style={{ color: MUTED }}>—</span>}
                  </Td>
                  <Td>
                    {user.gender === 'woman'
                      ? <FemaleRoundedIcon style={{ fontSize: 16, color: '#c084fc', verticalAlign: 'middle' }} />
                      : user.gender === 'man'
                        ? <MaleRoundedIcon style={{ fontSize: 16, color: BRAND, verticalAlign: 'middle' }} />
                        : <span style={{ color: MUTED }}>—</span>
                    }
                  </Td>
                  <Td style={{ color: MUTED }}>
                    {user.birthDate ? dayjs(user.birthDate).format("LL") : <span style={{ color: MUTED }}>—</span>}
                  </Td>
                  <Td style={{ color: MUTED, textTransform: 'capitalize' }}>
                    {user.type || '—'}
                  </Td>
                  <Td style={{ color: MUTED }}>
                    {user.connections ?? 0}
                  </Td>
                  <Td>
                    <StatusBadge status={user.proStatus} />
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 2 }}>
                      {user.proStatus === 'active' && (
                        <ActionBtn
                          title="Suspender"
                          color="#e05c5c"
                          onClick={() => dispatch(updateUserAction({ id: user.id, proStatus: 'waiting' }))}
                        >
                          <BlockRoundedIcon style={{ fontSize: 15 }} />
                        </ActionBtn>
                      )}
                      {user.proStatus === 'waiting' && (
                        <ActionBtn
                          title="Activar"
                          color="#5bbfb5"
                          onClick={() => dispatch(updateUserAction({ id: user.id, proStatus: 'active' }))}
                        >
                          <CheckRoundedIcon style={{ fontSize: 15 }} />
                        </ActionBtn>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </Box>
  );
};

export default Users;
